'use client';

import Link from 'next/link';
import { PlatformLogo } from '@/components/icons/platform-logo';
import { useRouter, useSearchParams } from 'next/navigation';
import { useLocale } from 'next-intl';
import { startTransition, useEffect, useState } from 'react';
import { getAuthPageCopy } from '@/i18n/auth-page-copy';
import { resolveAuthPageMode, type AuthPageMode } from './auth-page-mode';
import { PasswordResetForm } from './password-reset-form';
import { SignInForm } from './sign-in-form';
import { SignUpForm } from './sign-up-form';
import type { PublicSignupPage } from './types';

type AuthPageProps = {
  authBasePath?: string;
  defaultMode?: 'signIn' | 'signUp';
  signupPage?: PublicSignupPage | null;
  signInPath?: string;
  signUpPath?: string;
};

export function AuthPage({
  authBasePath = '/auth',
  defaultMode = 'signIn',
  signupPage,
  signInPath,
  signUpPath,
}: AuthPageProps) {
  const searchParams = useSearchParams();
  const router = useRouter();
  const locale = useLocale();
  const copy = getAuthPageCopy(locale);
  const marketingLocalePrefix = locale === 'en' ? '' : `/${locale}`;
  const inviteToken = searchParams.get('invite');
  const presetMode = searchParams.get('mode');
  const currentMode = resolveAuthPageMode({
    modeParam: presetMode,
    defaultMode,
  });
  const [mode, setMode] = useState<AuthPageMode>(currentMode);

  // The sign-in handoff lands on /auth/signing-in; with its loading shell
  // prefetched, the push commits at once, even on a slow network (#5559).
  useEffect(() => {
    router.prefetch('/auth/signing-in');
  }, [router]);

  useEffect(() => {
    if (mode !== currentMode) {
      startTransition(() => {
        setMode(currentMode);
      });
    }
  }, [mode, currentMode]);

  const resolvedSignInPath = signInPath ?? authBasePath;
  const resolvedSignUpPath = signUpPath ?? authBasePath;

  const replaceAuthRoute = (
    targetPath: string,
    nextMode: 'signIn' | 'signUp' | 'resetPassword'
  ) => {
    const params = new URLSearchParams(searchParams.toString());
    params.delete('mode');
    if (nextMode === 'resetPassword') {
      params.set('mode', 'reset');
    } else if (
      (nextMode === 'signIn' && targetPath === authBasePath) ||
      (nextMode === 'signUp' && targetPath === authBasePath)
    ) {
      params.set('mode', nextMode === 'signIn' ? 'signin' : 'signup');
    }
    const query = params.toString();
    router.replace(query ? `${targetPath}?${query}` : targetPath);
  };

  return (
    <div
      data-marketing-theme
      data-auth-page
      className="bg-marketing-surface text-marketing-fg font-marketing max-h-screen min-h-svh overflow-y-auto [font-synthesis:none]"
      dir={locale === 'ar' ? 'rtl' : 'ltr'}
    >
      <div className="flex min-h-svh w-full flex-col p-4 sm:px-8 sm:py-7">
        <header className="flex items-center">
          <Link
            href="/"
            aria-label={copy.goToHomePage}
            className="inline-flex items-center gap-1.5 rounded text-sm font-semibold focus-visible:outline-2 focus-visible:outline-offset-4"
          >
            <PlatformLogo
              size={18}
              className="text-marketing-fg dark:text-marketing-fg"
            />
            <span>IMAI Studio</span>
          </Link>
        </header>
        <main className="flex flex-1 items-center justify-center py-[clamp(1rem,3svh,3rem)]">
          <div className="flex w-full items-center justify-center">
            <div className="flex w-full max-w-[25rem] flex-col items-center justify-center 2xl:max-w-[38rem]">
              <section className="flex w-full flex-col items-center justify-center">
                {mode === 'signIn' ? (
                  <SignInForm
                    onToggleMode={() => {
                      replaceAuthRoute(resolvedSignUpPath, 'signUp');
                      setMode('signUp');
                    }}
                    onPasswordReset={() => {
                      replaceAuthRoute(resolvedSignInPath, 'resetPassword');
                      setMode('resetPassword');
                    }}
                    inviteToken={inviteToken}
                    authBasePath={authBasePath}
                  />
                ) : mode === 'signUp' ? (
                  <SignUpForm
                    onToggleMode={() => {
                      replaceAuthRoute(resolvedSignInPath, 'signIn');
                      setMode('signIn');
                    }}
                    inviteToken={inviteToken}
                    authBasePath={authBasePath}
                    signupPage={signupPage}
                  />
                ) : (
                  <PasswordResetForm
                    authBasePath={authBasePath}
                    onBackToSignIn={() => {
                      replaceAuthRoute(resolvedSignInPath, 'signIn');
                      setMode('signIn');
                    }}
                  />
                )}
              </section>
            </div>
          </div>
        </main>

        <footer className="text-muted-foreground flex flex-wrap items-center justify-center gap-x-4 gap-y-2 pb-2 text-sm sm:pb-4">
          <Link
            href={`${marketingLocalePrefix}/terms`}
            className="hover:text-foreground transition-colors"
          >
            {copy.termsOfUse}
          </Link>
          <span aria-hidden className="hidden sm:inline">
            |
          </span>
          <Link
            href={`${marketingLocalePrefix}/privacy`}
            className="hover:text-foreground transition-colors"
          >
            {copy.privacyPolicy}
          </Link>
        </footer>
      </div>
    </div>
  );
}
